// @ts-ignore
// eslint-disable-next-line
declare module "lodash";
import {
  Box,
  Stack,
  TextField,
  MenuItem,
  FormControl,
  Select,
  InputAdornment,
  Button,
} from "@mui/material";
import {
  DataGrid,
  GridRenderCellParams,
  useGridApiRef,
} from "@mui/x-data-grid";
import AccountSettingsIcon from "assets/svg/AccountSettingsIcon";
import AdminPageCardTitle from "components/AdminPageCardTitle/AdminPageCardTitle";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FETCH_ADMIN_USERS,
  FETCH_ADMIN_PROFILE,
} from "reduxStore/actions/authActionType";
import { RootState } from "reduxStore/store";
import { usersColumn } from "./dataGridDef";
import { useNavigate } from "react-router-dom";
import NoRowsTableDataOverlay from "components/NoRowsTableDataOverlay/NoRowsTableDataOverlay";
import PrimaryButton from "components/PrimaryButton/PrimaryButton";
import SearchIcon from "assets/svg/SearchIcon";

const UsersPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const apiRef = useGridApiRef();
  const { adminUser, loading } = useSelector(
    (state: RootState) => state.adminUser
  );
  const { profile } = useSelector((state: RootState) => state.adminProfile);
  const statusOptions = ["Active", "Inactive", "Suspended", "Unverified"];
  const roleOptions = ["Super Admin", "Admin", "User"];
  const [filters, setFilters] = useState({
    search_text: "",
    status: "",
    role: "",
  });
  const [filterData, setFilterData] = useState<any[]>([]);

  useEffect(() => {
    dispatch({ type: FETCH_ADMIN_USERS, payload: { page: 1, limit: 1000 } }); // Fetch all users (adjust limit as needed)
    dispatch({ type: FETCH_ADMIN_PROFILE });
  }, [dispatch]);

  useEffect(() => {
    let users = adminUser?.data?.users || [];
    if (filters.search_text) {
      const searchLower = filters.search_text.toLowerCase();
      users = users.filter((user: any) => {
        const name = `${user.firstName || ""} ${
          user.lastName || ""
        }`.toLowerCase();
        const email = (user.email || "").toLowerCase();
        return name.includes(searchLower) || email.includes(searchLower);
      });
    }
    if (filters.status) {
      users = users.filter(
        (user: any) =>
          (user.status || "").toLowerCase() === filters.status.toLowerCase()
      );
    }
    if (filters.role) {
      users = users.filter(
        (user: any) =>
          (user.role || "").toLowerCase() === filters.role.toLowerCase()
      );
    }
    setFilterData(users);
  }, [filters, adminUser]);

  const handleRowClick = (params: any) => {
    navigate(`/admin/users/${params.row.userId}`);
  };

  const updatedColumns = usersColumn.map((col) =>
    col.field === "id"
      ? {
          ...col,
          renderCell: (params: GridRenderCellParams) => {
            const { id } = params;
            const rowIndex = params.api.getRowIndexRelativeToVisibleRows(id);
            const page =
              apiRef?.current?.state?.pagination?.paginationModel?.page;
            const pageSize =
              apiRef?.current?.state?.pagination?.paginationModel?.pageSize ||
              10;
            return rowIndex + 1 + page * pageSize;
          },
        }
      : col
  );

  const handleFilterChange = (field: string, value: any) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleClearAll = () => {
    setFilters({ search_text: "", status: "", role: "" });
  };

  return (
    <Box sx={styles.container}>
      <Stack
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
        mb={2}
      >
        <AdminPageCardTitle
          title={"Users"}
          icon={<AccountSettingsIcon width={28} height={28} />}
        />
        {profile?.data?.personal_details?.role?.toLowerCase() ===
          "super admin" && (
          <PrimaryButton
            title="Create Admin User"
            onClick={() => navigate("/admin/user/create")}
          />
        )}
      </Stack>
      {/* Search Filter */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 2,
          flexWrap: "wrap",
          justifyContent: "flex-end",
          alignItems: { xs: "stretch", sm: "center" },
        }}
      >
        {/* Search Box */}
        <TextField
          placeholder="Search by Name or Email"
          value={filters.search_text}
          onChange={(e) => handleFilterChange("search_text", e.target.value)}
          size="small"
          sx={{
            maxWidth: 280,
            "& .MuiInputBase-input::placeholder": { fontSize: 13 },
            mb: { xs: 2, sm: 0 },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon width={20} height={20} />
              </InputAdornment>
            ),
          }}
        />
        {/* Dropdowns Row (responsive) */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 2,
            mb: { xs: 2, sm: 0 },
          }}
        >
          {/* Status Dropdown */}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <Select
              displayEmpty
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              sx={{ color: filters.status ? "inherit" : "#b0b0b0" }}
            >
              <MenuItem
                value=""
                disabled
                style={{ color: "#b0b0b0", fontSize: 13 }}
              >
                Status
              </MenuItem>
              {statusOptions.map((status) => (
                <MenuItem key={status} value={status} sx={{ fontSize: 13 }}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Role Dropdown */}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <Select
              displayEmpty
              value={filters.role}
              onChange={(e) => handleFilterChange("role", e.target.value)}
              sx={{ color: filters.role ? "inherit" : "#b0b0b0" }}
            >
              <MenuItem
                value=""
                disabled
                style={{ color: "#b0b0b0", fontSize: 13 }}
              >
                Role
              </MenuItem>
              {roleOptions.map((role) => (
                <MenuItem key={role} value={role} sx={{ fontSize: 13 }}>
                  {role}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        {/* Clear All Button (responsive) */}
        <Box
          sx={{
            width: { xs: "100%", sm: "auto" },
            display: "flex",
            justifyContent: { xs: "flex-end", sm: "unset" },
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={handleClearAll}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            Clear All
          </Button>
        </Box>
      </Box>
      <DataGrid
        rows={
          filters.search_text || filters.status || filters.role
            ? filterData
            : adminUser?.data?.users || []
        }
        apiRef={apiRef}
        columns={updatedColumns}
        getRowId={(row) => row.userId}
        loading={loading}
        slots={{
          noRowsOverlay: () => (
            <NoRowsTableDataOverlay title={"No users found"} />
          ),
        }}
        sx={{
          border: 0,
          "& .MuiDataGrid-row:hover": {
            cursor: "pointer",
          },
          "& .MuiDataGrid-cell:focus": {
            outline: "none",
          },
          "& .MuiDataGrid-cell:focus-within": {
            outline: "none",
          },
        }}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
        }}
        rowCount={
          filters.search_text || filters.status || filters.role
            ? filterData.length
            : adminUser?.data?.totalCount || 0
        }
        paginationMode="client"
        pageSizeOptions={[10]}
        disableRowSelectionOnClick
      />
    </Box>
  );
};
export default UsersPage;
const styles = {
  container: {
    flexGrow: 1,
    borderRadius: 3,
    boxShadow: `rgba(8, 60, 61, 0.27) 0px 1px 5px 0px, rgba(8, 60, 61, 0.06) 0px 1px 5px 0px`,
    backgroundColor: "rgb(255, 255, 255)",
    p: 3,
    ml: { xs: 0, md: -5 },
  },
};
